import { FC } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { RefreshOrderSettingsViewModel, WaterfallViewModel } from '../../models/waterfall';

interface RefreshOrderSettingsFormProps {
  settings: RefreshOrderSettingsViewModel | null | undefined;
  onChange: (settings: RefreshOrderSettingsViewModel) => void;
  waterfalls: WaterfallViewModel[];
  disabled?: boolean;
  alwaysSendParentWaterfallDetailInWebhookEvents: boolean;
  onAlwaysSendParentDetailChange: (value: boolean) => void;
}

export const RefreshOrderSettingsForm: FC<RefreshOrderSettingsFormProps> = ({
  settings,
  onChange,
  waterfalls,
  disabled = false,
  alwaysSendParentWaterfallDetailInWebhookEvents,
  onAlwaysSendParentDetailChange,
}) => {
  const waterfallOptions = waterfalls
    .filter((w) => !w.archived)
    .map((w) => ({
      label: w.name,
      value: w.waterfallId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleTimeoutChange = (value: number | null) => {
    onChange({
      ...settings,
      timeoutInMinutes: value,
    });
  };

  const handleOnCancelWaterfallChange = (value: number | null) => {
    onChange({
      ...settings,
      onCancelWaterfallId: value,
    });
  };

  const handleOnDisconnectWaterfallChange = (value: number | null) => {
    onChange({
      ...settings,
      onDisconnectWaterfallId: value,
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <h3 className="text-lg font-medium mb-4">Refresh Order Settings</h3>
      <p className="text-sm text-gray-600 mb-4">
        Configure timeout and waterfall chaining behavior for refresh orders created from this waterfall.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="field">
          <label htmlFor="refreshTimeoutInMinutes" className="font-medium block mb-2">
            Timeout (Minutes)
          </label>
          <InputNumber
            id="refreshTimeoutInMinutes"
            value={settings?.timeoutInMinutes ?? null}
            onValueChange={(e) => handleTimeoutChange(e.value ?? null)}
            placeholder="e.g., 1440 for 24 hours"
            className="w-full"
            disabled={disabled}
            useGrouping={false}
            min={1}
          />
          <small className="text-gray-500">
            Time before refresh order is auto-cancelled
          </small>
        </div>
        <div className="field">
          <label htmlFor="refreshOnCancelWaterfallId" className="font-medium block mb-2">
            On Cancel Waterfall
          </label>
          <Dropdown
            id="refreshOnCancelWaterfallId"
            value={settings?.onCancelWaterfallId ?? null}
            onChange={(e) => handleOnCancelWaterfallChange(e.value)}
            options={waterfallOptions}
            placeholder="Select waterfall"
            className="w-full"
            disabled={disabled}
            showClear
            appendTo={document.body}
          />
          <small className="text-gray-500">
            Waterfall to execute when refresh order is cancelled
          </small>
        </div>
        <div className="field">
          <label htmlFor="refreshOnDisconnectWaterfallId" className="font-medium block mb-2">
            On Disconnect Waterfall
          </label>
          <Dropdown
            id="refreshOnDisconnectWaterfallId"
            value={settings?.onDisconnectWaterfallId ?? null}
            onChange={(e) => handleOnDisconnectWaterfallChange(e.value)}
            options={waterfallOptions}
            placeholder="Select waterfall"
            className="w-full"
            disabled={disabled}
            showClear
            appendTo={document.body}
          />
          <small className="text-gray-500">
            Waterfall to execute when refresh order is disconnected
          </small>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="alwaysSendParentWaterfallDetailInWebhookEvents"
            checked={alwaysSendParentWaterfallDetailInWebhookEvents}
            onChange={(e) => onAlwaysSendParentDetailChange(e.checked ?? false)}
            disabled={disabled}
          />
          <label htmlFor="alwaysSendParentWaterfallDetailInWebhookEvents" className="font-medium cursor-pointer">
            Always Send Parent Waterfall Detail in Webhook Events
          </label>
        </div>
        <small className="text-gray-500 ml-6">
          When enabled, webhook events will include the root parent waterfall detail instead of the child waterfall detail.
        </small>
      </div>
    </div>
  );
};
